import React, { memo } from 'react';
import type { ColumnDef, Row } from '@tanstack/react-table';
import { activeCellProps } from './dom';
import { type Pin, pinCellProps } from './pinning';
import { renderCell } from './renderCell';

export type SpreadsheetCellProps = {
  pin: Pin;
  width: number;
  active: boolean;
  activeCellRef: React.MutableRefObject<HTMLTableCellElement | null>;
  /** Selects the cell on a press inside it. Without it the cell can't be picked. */
  onSelect?: () => void;
  /** When set, a paste or clear was refused here; a new value flashes it again. */
  refusedAt?: number;
  /** The cell last copied, until Escape. */
  copied?: boolean;
  children: React.ReactNode;
};

/**
 * One grid cell, wherever it sits: the active-cell outline, the pinned
 * column's place and shadow, and the marks a copy or refused write leaves.
 */
export const SpreadsheetCell: React.FC<SpreadsheetCellProps> = ({
  pin,
  width,
  active,
  activeCellRef,
  onSelect,
  refusedAt,
  copied = false,
  children,
}) => {
  const pinned = pinCellProps(pin, width);
  return (
    <td
      ref={active ? activeCellRef : undefined}
      {...(active ? activeCellProps : {})}
      style={pinned.style}
      onMouseDown={
        onSelect &&
        (e => {
          // React passes events up from a cell's open dropdown or dialog,
          // which live elsewhere in the page. Selecting the cell there
          // re-renders and can scroll the grid under the list, so the press
          // and release land on different options and nothing ticks.
          if (!e.currentTarget.contains(e.target as Node)) return;
          onSelect();
        })
      }
      className={`relative max-w-0 border-b border-r border-b-slate-200 border-r-slate-100 p-0 last:border-r-0 ${pinned.className} ${active ? 'outline outline-2 -outline-offset-2 outline-primary' : ''}`}
    >
      {refusedAt !== undefined && (
        <span
          key={refusedAt}
          aria-hidden
          className='refused-flash pointer-events-none absolute inset-0'
        />
      )}
      {copied && (
        <span
          aria-hidden
          className='marching-ants pointer-events-none absolute inset-0'
        />
      )}
      {children}
    </td>
  );
};

type SpreadsheetRowProps<T> = {
  row: Row<T>;
  rowIndex: number;
  className?: string;
  /** Not read here: a new set from the caller redraws every row. */
  columns: ColumnDef<T>[];
  /** One per visible column; a new set when the visible columns change. */
  pins: readonly Pin[];
  /** The cursor's column in this row, or -1. */
  activeCol: number;
  activeCellRef: React.MutableRefObject<HTMLTableCellElement | null>;
  /** The column refused in this row, and when; a new time flashes it again. */
  refusedColumnId: string | null;
  refusedAt: number;
  /** The column copied in this row. */
  copiedColumnId: string | null;
  onSelectCell: (rowIndex: number, colIndex: number) => void;
};

function SpreadsheetRowInner<T>({
  row,
  rowIndex,
  className,
  pins,
  activeCol,
  activeCellRef,
  refusedColumnId,
  refusedAt,
  copiedColumnId,
  onSelectCell,
}: SpreadsheetRowProps<T>) {
  return (
    // An opaque row colour, which pinned cells take on so the cells scrolling
    // under them do not show through.
    <tr className={`${className || 'bg-white'} hover:bg-slate-50`}>
      {row.getVisibleCells().map((cell, colIndex) => (
        <SpreadsheetCell
          key={cell.id}
          pin={pins[colIndex]}
          width={cell.column.getSize()}
          active={activeCol === colIndex}
          activeCellRef={activeCellRef}
          onSelect={() => onSelectCell(rowIndex, colIndex)}
          refusedAt={refusedColumnId === cell.column.id ? refusedAt : undefined}
          copied={copiedColumnId === cell.column.id}
        >
          {renderCell(cell.column.columnDef.cell, cell.getContext())}
        </SpreadsheetCell>
      ))}
    </tr>
  );
}

/**
 * Compared by the row's data, not the Row object: the table rebuilds every
 * Row whenever its data array changes, so an edit to one row would
 * otherwise redraw them all. Everything else is compared as given, so the
 * cursor moving redraws the row it left and the row it entered, not every row.
 */
function sameRow<T>(
  prev: SpreadsheetRowProps<T>,
  next: SpreadsheetRowProps<T>
): boolean {
  return (Object.keys(next) as (keyof SpreadsheetRowProps<T>)[]).every(key =>
    key === 'row'
      ? prev.row.id === next.row.id && prev.row.original === next.row.original
      : prev[key] === next[key]
  );
}

/** One row of cells, memoised by `sameRow`. */
export const SpreadsheetRow = memo(
  SpreadsheetRowInner,
  sameRow
) as typeof SpreadsheetRowInner;
